import {
  S3Client,
  CreateMultipartUploadCommand,
  UploadPartCommand,
  CompleteMultipartUploadCommand,
  AbortMultipartUploadCommand,
} from "@aws-sdk/client-s3"

// Initialize S3 Client with forcePathStyle enabled for DigitalOcean Spaces
const s3Client = new S3Client({
  endpoint: process.env.REACT_APP_S3_ENDPOINT,
  region: process.env.REACT_APP_S3_REGION,
  credentials: {
    accessKeyId: process.env.REACT_APP_S3_ACCESS_KEY_ID,
    secretAccessKey: process.env.REACT_APP_S3_SECRET_ACCESS_KEY,
  },
  forcePathStyle: true, // Enable path-style addressing
})

export async function UploadFileToS3({
  file,
  bucketName = "internal-chatbot-storage",
  folder = "",
  fileName = "",
  contentType = "audio/mpeg", // Set default contentType for MP3 files
  onProgress = null,
  abortController = null,
  onUploadIdGenerated = null,
}) {
  try {
    if (!file) {
      throw new Error("No file provided")
    }
    console.log("Starting file upload process")

    const uploadFileName = fileName || file.name
    const uploadContentType = contentType || file.type || "audio/mpeg" // Use default contentType if not provided

    let key = uploadFileName
    if (folder) {
      const cleanFolder = folder.replace(/^\/|\/$/g, "")
      key = `${cleanFolder}/${uploadFileName}`
    }

    // Note: For very small files, we'll still use multipart upload
    // to maintain consistent code flow and error handling

    const result = await multipartUpload({
      file,
      bucketName,
      key,
      contentType: uploadContentType,
      onProgress,
      abortController,
      onUploadIdGenerated,
    })

    return result
  } catch (error) {
    if (error.name === "AbortError") {
      console.log("Upload was aborted by user")
      return {
        success: false,
        aborted: true,
        error: "Upload aborted by user",
      }
    }

    console.error("S3 upload error:", error)
    return {
      success: false,
      error: error.message || "Failed to upload file",
      details: error,
    }
  }
}

async function multipartUpload({
  file,
  bucketName,
  key,
  contentType,
  chunkSize = 5 * 1024 * 1024, // Default chunk size (5MB) - but we'll handle smaller files too
  onProgress = null,
  abortController = null,
  onUploadIdGenerated = null,
}) {
  let uploadId
  const uploadedParts = []

  // Check if AbortController signal is already aborted
  if (abortController && abortController.signal.aborted) {
    throw new Error("Upload aborted before starting")
  }

  try {
    console.log(`Initiating multipart upload for ${key} to ${bucketName}`)
    // Step 1: Create multipart upload to get an uploadId
    const createCommand = new CreateMultipartUploadCommand({
      Bucket: bucketName,
      Key: key,
      ContentType: contentType,
      ACL: "public-read",
    })

    const { UploadId } = await s3Client.send(createCommand)
    if (!UploadId) {
      throw new Error("Failed to get upload ID")
    }
    uploadId = UploadId
    console.log(`Upload ID received: ${uploadId}`)

    // Notify about the upload ID for potential cancellation
    if (onUploadIdGenerated && typeof onUploadIdGenerated === "function") {
      onUploadIdGenerated(uploadId)
    }

    // Check if aborted after getting upload ID
    if (abortController && abortController.signal.aborted) {
      throw new DOMException("Upload aborted by user", "AbortError")
    }

    // Step 2: Upload parts
    // For small files (<5MB), we'll use a single part
    // For larger files, split into 5MB chunks
    const effectiveChunkSize =
      file.size < 5 * 1024 * 1024 ? file.size : chunkSize
    const numParts = Math.ceil(file.size / effectiveChunkSize)
    console.log(`File will be split into ${numParts} parts`)

    // Upload parts sequentially to avoid overwhelming the network
    for (let partNumber = 1; partNumber <= numParts; partNumber++) {
      // Check if the upload was aborted
      if (abortController && abortController.signal.aborted) {
        throw new DOMException("Upload aborted by user", "AbortError")
      }

      const start = (partNumber - 1) * effectiveChunkSize
      const end = Math.min(partNumber * effectiveChunkSize, file.size)
      const chunk = file.slice(start, end)
      const chunkBuffer = await blobToBuffer(chunk)

      console.log(
        `Uploading part ${partNumber}/${numParts}, size: ${chunkBuffer.length} bytes`
      )

      const uploadPartCommand = new UploadPartCommand({
        Bucket: bucketName,
        Key: key,
        UploadId: uploadId,
        PartNumber: partNumber,
        Body: chunkBuffer,
        ContentLength: chunkBuffer.length,
      })

      try {
        const response = await s3Client.send(uploadPartCommand)

        if (!response || !response.ETag) {
          throw new Error(
            `Upload part ${partNumber} did not return a valid ETag`
          )
        }

        // The CompleteMultipartUpload API expects the ETag to be quoted
        // Make sure it's properly formatted with quotes
        let eTag = response.ETag
        if (!eTag.startsWith('"') && !eTag.endsWith('"')) {
          eTag = `"${eTag.replace(/^"|"$/g, "")}"`
        }

        uploadedParts.push({
          PartNumber: partNumber,
          ETag: eTag,
        })

        // Update progress if callback provided
        if (onProgress && typeof onProgress === "function") {
          const percentComplete = (partNumber / numParts) * 100
          onProgress(percentComplete)
        }

        console.log(`Part ${partNumber} uploaded successfully, ETag: ${eTag}`)
      } catch (partError) {
        // Check if this was an abort error
        if (abortController && abortController.signal.aborted) {
          throw new DOMException("Upload aborted by user", "AbortError")
        }

        console.error(`Error uploading part ${partNumber}:`, partError)
        throw new Error(
          `Failed to upload part ${partNumber}: ${partError.message}`
        )
      }
    }

    // Final check for abort before completing
    if (abortController && abortController.signal.aborted) {
      throw new DOMException("Upload aborted by user", "AbortError")
    }

    // Verify all parts are present and have ETags before completing
    if (uploadedParts.length !== numParts) {
      throw new Error(
        `Missing parts: expected ${numParts}, got ${uploadedParts.length}`
      )
    }

    // Sort parts by part number as required by S3 API
    uploadedParts.sort((a, b) => a.PartNumber - b.PartNumber)

    console.log(
      `All ${numParts} parts uploaded successfully, completing multipart upload`
    )
    console.log(`Parts list: ${JSON.stringify(uploadedParts)}`)

    // Step 3: Complete multipart upload
    const completeCommand = new CompleteMultipartUploadCommand({
      Bucket: bucketName,
      Key: key,
      UploadId: uploadId,
      MultipartUpload: {
        Parts: uploadedParts,
      },
    })

    const completeResponse = await s3Client.send(completeCommand)
    console.log("Multipart upload completed successfully")

    // Update progress to 100% on completion
    if (onProgress && typeof onProgress === "function") {
      onProgress(100)
    }

    let fileUrl =
      completeResponse.Location ||
      `https://${bucketName}.sgp1.digitaloceanspaces.com/${key}`

    if (!fileUrl.startsWith("http://") && !fileUrl.startsWith("https://")) {
      fileUrl = `https://${fileUrl}`
    }

    return {
      success: true,
      url: fileUrl,
      key,
      etag: completeResponse.ETag,
      location: completeResponse.Location,
    }
  } catch (error) {
    console.error("Multipart upload failed:", error)

    // If we have an uploadId and it's not an abort error, attempt to abort the multipart upload
    if (uploadId && error.name !== "AbortError") {
      try {
        console.log(`Aborting multipart upload with ID: ${uploadId}`)
        const abortCommand = new AbortMultipartUploadCommand({
          Bucket: bucketName,
          Key: key,
          UploadId: uploadId,
        })
        await s3Client.send(abortCommand)
        console.log("Multipart upload aborted successfully")
      } catch (abortError) {
        console.error("Failed to abort multipart upload:", abortError)
      }
    } else if (uploadId && error.name === "AbortError") {
      // The user intentionally canceled the upload
      try {
        console.log(`User aborted upload with ID: ${uploadId}`)
        const abortCommand = new AbortMultipartUploadCommand({
          Bucket: bucketName,
          Key: key,
          UploadId: uploadId,
        })
        await s3Client.send(abortCommand)
        console.log("Multipart upload aborted successfully by user request")
      } catch (abortError) {
        console.error(
          "Failed to abort multipart upload after user cancel:",
          abortError
        )
      }

      // Return an object indicating successful abortion
      return {
        success: false,
        aborted: true,
        error: "Upload aborted by user",
      }
    }

    throw error
  }
}

export async function DeleteFileFromS3({
  bucketName = "internal-chatbot-storage",
  fileUrl,
  directKey = null,
}) {
  try {
    // If direct key is provided, use it instead of trying to extract from URL
    if (directKey) {
      console.log(`DeleteFileFromS3: Using provided direct key: ${directKey}`)

      // Import the DeleteObjectCommand from AWS SDK
      const { DeleteObjectCommand } = await import("@aws-sdk/client-s3")

      const deleteCommand = new DeleteObjectCommand({
        Bucket: bucketName,
        Key: directKey,
      })

      console.log(`Sending delete command to S3 for key: ${directKey}`)
      const deleteResult = await s3Client.send(deleteCommand)
      console.log("S3 delete response:", deleteResult)
      console.log("File deleted successfully using direct key")

      return {
        success: true,
        key: directKey,
        response: deleteResult,
      }
    }

    // Continue with URL-based deletion if no directKey provided
    if (!fileUrl) {
      console.error("No file URL provided for deletion")
      return {
        success: false,
        error: "No file URL or direct key provided",
      }
    }

    console.log(
      `DeleteFileFromS3: Attempting to delete file: ${fileUrl} from bucket: ${bucketName}`
    )

    // Extract key from URL - handle different URL formats
    let key = null

    // For DigitalOcean Spaces, URLs might look like:
    // 1. https://bucketname.region.digitaloceanspaces.com/path/file.ext
    // 2. https://region.digitaloceanspaces.com/bucketname/path/file.ext
    try {
      const urlObj = new URL(fileUrl)
      console.log("URL parsing successful, pathname:", urlObj.pathname)

      // Format 1: bucket is in domain
      if (urlObj.hostname.startsWith(`${bucketName}.`)) {
        key = urlObj.pathname.replace(/^\//, "") // Remove leading slash
        console.log(`Format 1 detected, extracted key: ${key}`)
      }
      // Format 2: bucket is in path
      else if (urlObj.pathname.startsWith(`/${bucketName}/`)) {
        key = urlObj.pathname.replace(new RegExp(`^/${bucketName}/`), "")
        console.log(`Format 2 detected, extracted key: ${key}`)
      }
      // Audio files specific format handling
      else if (
        fileUrl.includes("/audio/") &&
        urlObj.pathname.includes("/audio/")
      ) {
        // The key might just be 'audio/filename.mp3'
        const audioMatch = urlObj.pathname.match(/\/audio\/(.+)$/)
        if (audioMatch && audioMatch[1]) {
          key = `audio/${audioMatch[1]}`
          console.log(`Audio file format detected, extracted key: ${key}`)
        }
      }
    } catch (error) {
      console.error("Error parsing URL:", error)
    }

    // Fallback extraction methods if above failed
    if (!key) {
      console.log("Attempting fallback key extraction methods")

      // Try simple pattern matching for audio files
      if (fileUrl.includes("/audio/")) {
        const audioPattern = /\/audio\/([^?#]+)/
        const match = fileUrl.match(audioPattern)
        if (match && match[1]) {
          key = `audio/${match[1]}`
          console.log(`Extracted audio key using pattern: ${key}`)
        }
      }

      // Generic bucket pattern fallback
      if (!key) {
        const bucketPattern = new RegExp(`https?://[^/]+/${bucketName}/(.+)`)
        const match = fileUrl.match(bucketPattern)
        if (match && match[1]) {
          key = match[1]
          console.log(`Extracted key using bucket pattern: ${key}`)
        }
      }
    }

    if (!key) {
      console.error("Could not extract valid key from file URL:", fileUrl)
      return {
        success: false,
        error: "Failed to extract valid file key from URL",
      }
    }

    console.log(
      `Proceeding with deletion. Final key: ${key}, Bucket: ${bucketName}`
    )

    // Import the DeleteObjectCommand from AWS SDK
    const { DeleteObjectCommand } = await import("@aws-sdk/client-s3")

    const deleteCommand = new DeleteObjectCommand({
      Bucket: bucketName,
      Key: key,
    })

    console.log("Sending delete command to S3...")
    const deleteResult = await s3Client.send(deleteCommand)
    console.log("S3 delete response:", deleteResult)
    console.log("File deleted successfully")

    return {
      success: true,
      key: key,
      response: deleteResult,
    }
  } catch (error) {
    console.error("Failed to delete file from S3:", error)
    return {
      success: false,
      error: error.message || "Failed to delete file",
      details: error,
    }
  }
}

async function blobToBuffer(blob) {
  try {
    if (typeof blob.arrayBuffer === "function") {
      return new Uint8Array(await blob.arrayBuffer())
    }

    return new Promise((resolve, reject) => {
      const reader = new FileReader()
      reader.onload = () => resolve(new Uint8Array(reader.result))
      reader.onerror = reject
      reader.readAsArrayBuffer(blob)
    })
  } catch (error) {
    throw new Error(`Failed to convert blob to buffer: ${error.message}`)
  }
}
